import { AuthScreenShell } from '@/components/auth/AuthScreenShell'
import { AUTH_COLORS, authScreenStyles as styles } from '@/components/auth/authScreenStyles'
import { BodyText, PageTitle } from '@/components/ui/Typography'
import { URLS } from '@/lib/constants'
import { clearFreshSignup, markFreshSignup } from '@/lib/freshSignup'
import { Ionicons } from '@expo/vector-icons'
import { useRouter } from 'expo-router'
import React, { useState } from 'react'
import {
  ActivityIndicator,
  Alert,
  Linking,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import { useAuth } from '../../lib/AuthContext'

export default function SignUpScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const { signUp, signInWithGoogle } = useAuth()
  const router = useRouter()

  const validateForm = () => {
    if (!email || !password || !firstName || !lastName) {
      Alert.alert('Error', 'Please fill in all fields')
      return false
    }

    if (password.length < 6) {
      Alert.alert('Error', 'Password must be at least 6 characters long')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      Alert.alert('Error', 'Please enter a valid email address')
      return false
    }

    return true
  }

  const handleSignUp = async () => {
    if (!validateForm()) return

    setLoading(true)
    // Marked before the call: the session can land (and the auth layout
    // redirect fire) while signUp is still awaiting. Cleared on failure so a
    // follow-up login never inherits the plans-screen routing.
    markFreshSignup()
    try {
      const { error } = await signUp(email, password, {
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`.trim(),
      })

      if (error) {
        clearFreshSignup()
        Alert.alert('Sign Up Failed', error.message)
      }
    } catch (error: any) {
      clearFreshSignup()
      Alert.alert('Error', error.message || 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setLoading(true)
    try {
      const { error } = await signInWithGoogle()
      if (error) {
        Alert.alert('Google Sign Up Failed', error.message)
      }
    } catch (error: any) {
      Alert.alert('Error', error.message || 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const navigateToLogin = () => {
    router.push('/auth/login' as any)
  }

  return (
    <AuthScreenShell onBack={() => router.back()}>
      <View style={styles.card}>
        <PageTitle ta="center" fontWeight="700" color={AUTH_COLORS.text} mb={8}>
          Create account
        </PageTitle>
        <BodyText ta="center" color={AUTH_COLORS.muted} mb={28}>
          Join Open Studio
        </BodyText>

        <View style={styles.form}>
          <View style={styles.row}>
            <View style={[styles.inputContainer, styles.halfWidth]}>
              <Text style={styles.label}>First Name</Text>
              <TextInput
                style={styles.input}
                placeholder="First name"
                placeholderTextColor={AUTH_COLORS.muted}
                value={firstName}
                onChangeText={setFirstName}
                autoCapitalize="words"
                editable={!loading}
              />
            </View>

            <View style={[styles.inputContainer, styles.halfWidth]}>
              <Text style={styles.label}>Last Name</Text>
              <TextInput
                style={styles.input}
                placeholder="Last name"
                placeholderTextColor={AUTH_COLORS.muted}
                value={lastName}
                onChangeText={setLastName}
                autoCapitalize="words"
                editable={!loading}
              />
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              placeholder="Enter your email"
              placeholderTextColor={AUTH_COLORS.muted}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!loading}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Password</Text>
            <View style={styles.passwordField}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                placeholder="Create a password"
                placeholderTextColor={AUTH_COLORS.muted}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                editable={!loading}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TouchableOpacity
                style={styles.passwordToggle}
                onPress={() => setShowPassword((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
                hitSlop={8}
                disabled={loading}
              >
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={22}
                  color={AUTH_COLORS.muted}
                />
              </TouchableOpacity>
            </View>
          </View>

          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={handleSignUp}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.buttonText}>Create Account</Text>
            )}
          </TouchableOpacity>

          <Text style={[styles.linkMuted, { textAlign: 'center', lineHeight: 20 }]}>
            By creating an account, you agree to our{' '}
            <Text
              style={styles.link}
              onPress={() => void Linking.openURL(URLS.TERMS)}
            >
              Terms of Service
            </Text>
            {' '}and{' '}
            <Text
              style={styles.link}
              onPress={() => void Linking.openURL(URLS.PRIVACY)}
            >
              Privacy Policy
            </Text>
            .
          </Text>

          <View style={styles.footerRow}>
            <Text style={styles.linkMuted}>Already have an account? </Text>
            <TouchableOpacity onPress={navigateToLogin} disabled={loading}>
              <Text style={styles.link}>Sign In</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </AuthScreenShell>
  )
}
